import { ButtonBase, Stack, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { getProgramStatus } from '@shared/utilities';
import { DateTime } from 'luxon';
import { Button, Card, HSpacer, Text, TextBadge } from '@/components/DesignSystem';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import {
  compareProgramsByStatusThenEndDate, getProgramPeriodText,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers';

interface RewardsProgramsTableProps {
  idsWithPointsEngineRun?: string[],
  isInitializing?: boolean,
  onDelete: (id: string) => void,
  onInitializeFarmers: () => void,
  onSelect: (id: string) => void,
  programs: ApiRewardsProgram[],
}

export const RewardsProgramsTable = ({
  idsWithPointsEngineRun = [],
  isInitializing,
  onDelete,
  onInitializeFarmers,
  onSelect,
  programs,
}: RewardsProgramsTableProps) => {
  const theme = useTheme();

  const areAnyPending = useMemo(() => (
    programs.some((program) => (
      getProgramStatus(program) !== ProgramStatus.Expired
      && !idsWithPointsEngineRun.includes(program.id)
    ))
  ), [programs, idsWithPointsEngineRun]);

  const areAnyExpired = useMemo(() => (
    programs.some((p) => (
      getProgramStatus(p) === ProgramStatus.Expired
    ))
  ), [programs]);

  return (
    <Stack spacing={1} width="100%">
      {[...programs]
        .sort(compareProgramsByStatusThenEndDate)
        .map((program) => {
          const status = getProgramStatus(program);
          let statusColor;
          let statusTextColor = undefined;
          switch (status) {
            case ProgramStatus.Active:
              statusColor = theme.palette.success.main;
              break;
            case ProgramStatus.Future:
              statusColor = theme.palette.info.main;
              break;
            case ProgramStatus.Expired:
              statusColor = theme.palette.outline;
              statusTextColor = theme.palette.text.primary;
              break;
            default:
              statusColor = undefined;
          }
          const createdDateText = (
            DateTime.fromJSDate(program.createdAt).toFormat('MMM d, yyyy')
          );
          const isPending = (
            status !== ProgramStatus.Expired
            && !idsWithPointsEngineRun.includes(program.id)
          );
          const canDelete = status === ProgramStatus.Future;
          const canInitialize = (
            status === ProgramStatus.Active
            && areAnyExpired
          );

          return (
            <Card
              cardContentStyle={{ '&:last-child': { paddingBottom: 0 }, padding: 0 }}
              elevation={0}
              key={program.id}
              testID={`rewards-program-card-${program.id}`}
            >
              <ButtonBase
                disableRipple
                onClick={() => onSelect(program.id)}
                sx={{ p: '16px 0px 16px 16px', height: '52px', width: '100%' }}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack alignItems="center" direction="row">
                    <Stack
                      alignItems="center"
                      direction="row"
                      width={areAnyPending ? '163px' : '100px'}
                    >
                      {!!status && (
                        <TextBadge
                          backgroundColor={statusColor}
                          text={status}
                          textColor={statusTextColor}
                        />
                      )}
                      {isPending && (
                        <>
                          <HSpacer size="3"/>
                          <TextBadge
                            backgroundColor={theme.palette.outline}
                            text="PENDING"
                            textColor={theme.palette.text.primary}
                          />
                        </>
                      )}
                    </Stack>
                    <HSpacer size="3"/>
                    <Stack alignItems="center" direction="row" width="283px">
                      <Text sx={{ color: theme.palette.text.secondary }}>
                        Program period:
                      </Text>
                      <HSpacer size="2"/>
                      <Text>
                        {getProgramPeriodText(program)}
                      </Text>
                    </Stack>
                    <HSpacer size="8"/>
                    <Text sx={{ color: theme.palette.text.secondary }}>
                      Created:
                    </Text>
                    <HSpacer size="2"/>
                    <Text>
                      {createdDateText}
                    </Text>
                  </Stack>
                  <Stack direction="row">
                    {canInitialize && (
                      <Button
                        disabled={isInitializing}
                        onClick={(e) => {
                          e.stopPropagation();
                          onInitializeFarmers();
                        }}
                        startIcon={<GroupAddOutlined />}
                        testID={`rewards-program-initialize-rewards-button-${program.id}`}
                        variant="text"
                      >
                        Initialize Farmers
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(program.id);
                        }}
                        startIcon={<DeleteOutline/>}
                        testID={`rewards-program-delete-button-${program.id}`}
                        variant="text"
                      >
                        Delete
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </ButtonBase>
            </Card>
          );
        })}
    </Stack>
  );
};
